import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useAppState} from '../../../state'
import styled from 'styled-components'
import {Label} from '../../Atoms/Forms'
import {EquipmentItem, EquipmentSelectionMap} from '../../../config/utils'
import {FixedEquipmentTypes} from '../../../state/rest'
import {capitalizeFirstLetter} from '../../../Components/Atoms/Utils'
import {colors} from '../../../sharedComponents/colors'

interface DevicesListProps {
  controller: {
    selectionMapOfDevices: EquipmentSelectionMap
    toggleAll: (category: FixedEquipmentTypes) => void
    toggleDevices: (category: FixedEquipmentTypes, id: string | string[]) => void
    resetCategory: (category: FixedEquipmentTypes) => void
    getSelectedDevices?: () => string[]
    getSelectedDevicesByCategory?: (category: FixedEquipmentTypes) => EquipmentItem[]
    isSelected: (_category: FixedEquipmentTypes, id: string) => boolean
  }
  cyKey?: string
  label: string
}

const DevicesList = ({controller, cyKey, label}: DevicesListProps) => {
  const {state} = useAppState()
  const {t} = useTranslation('alarms')
  const chain = state.chainsById[state.selectedChainId!]

  // Memoize categories derived from the controller map
  const equipmentCategories = useMemo(() => Object.keys(controller.selectionMapOfDevices) as FixedEquipmentTypes[], [
    controller.selectionMapOfDevices
  ])

  const deviceLabels = useMemo(
    () => ({
      storage: t('alarms:labels.deviceType.storage', 'Storage'),
      dishwasher: t('alarms:labels.deviceType.dishwasher', 'Dishwasher'),
      cooler: t('alarms:labels.deviceType.cooler', 'Cooler'),
      freezer: t('alarms:labels.deviceType.freezer', 'Freezer')
    }),
    [t]
  )

  if (!chain) {
    return <div>{t('common:errors.undefinedError', 'Something went wrong')}</div>
  }

  return (
    <Container>
      <Label style={{marginBottom: 2}}>{label}</Label>
      <Grid>
        {equipmentCategories.map(
          cat =>
            cat !== FixedEquipmentTypes.WasteScale && (
              <EquipmentDropdown
                key={cat}
                label={deviceLabels[cat] ?? capitalizeFirstLetter(cat)}
                category={cat}
                controller={controller}
                t={t}
              />
            )
        )}
      </Grid>
    </Container>
  )
}

interface EquipmentDropdownProps {
  label: string
  category: FixedEquipmentTypes
  controller: {
    selectionMapOfDevices: EquipmentSelectionMap
    toggleAll: (category: FixedEquipmentTypes) => void
    toggleDevices: (category: FixedEquipmentTypes, id: string | string[]) => void
    resetCategory: (category: FixedEquipmentTypes) => void
    getSelectedDevices?: () => string[]
    getSelectedDevicesByCategory?: (category: FixedEquipmentTypes) => EquipmentItem[]
    isSelected: (_category: FixedEquipmentTypes, id: string) => boolean
  }
  t: (key: string, defaultValue: string) => string
}

const EquipmentDropdown = ({label, category, controller, t}: EquipmentDropdownProps) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const options: EquipmentItem[] = controller.selectionMapOfDevices[category] || []

  const filtered = () => {
    const q = search.trim().toLowerCase()
    if (!q) return options
    return options.filter(o => (o.name || '').toLowerCase().includes(q) || (o.siteName || '').toLowerCase().includes(q))
  }

  const selectedInCategory = options.filter(o => o.selected).map(o => o.id)

  const allFilteredSelected = filtered().length > 0 && filtered().every(e => e.selected)

  const handleToggleAllFiltered = useCallback(() => {
    if (filtered().length === 0) return
    const every = filtered().every(e => e.selected)
    if (every) {
      // deselect only filtered that are selected
      const ids = filtered()
        .filter(e => e.selected)
        .map(e => e.id)
      if (ids.length) controller.toggleDevices(category, ids)
    } else {
      // select only filtered that are not selected
      const ids = filtered()
        .filter(e => !e.selected)
        .map(e => e.id)
      if (ids.length) controller.toggleDevices(category, ids)
    }
  }, [filtered, controller, category])

  const handleToggleOne = useCallback((id: string) => controller.toggleDevices(category, id), [controller, category])

  const handleClearCategory = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      controller.resetCategory(category)
    },
    [controller, category]
  )

  return (
    <DropdownWrapper ref={wrapperRef}>
      <Label>{capitalizeFirstLetter(label)}</Label>
      <Row>
        <DropdownButton onClick={() => setOpen(o => !o)} data-cy={`${label.toLowerCase()}-dropdown`}>
          {selectedInCategory.length
            ? `${selectedInCategory.length} ${t('alarms:labels.selected', 'selected')}`
            : t('alarms:labels.selectEquipment', 'Select device')}
        </DropdownButton>
        <ClearButton
          onClick={handleClearCategory}
          title="Clear selection"
          disabled={selectedInCategory.length === 0}
          style={{
            opacity: selectedInCategory.length === 0 ? 0.4 : 1,
            cursor: selectedInCategory.length === 0 ? 'not-allowed' : 'pointer'
          }}
        >
          ×
        </ClearButton>
      </Row>
      {open && (
        <Menu>
          <SearchInput
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('alarms:placeholders.searchEquipment', 'Search device')}
            autoFocus
            onClick={e => e.stopPropagation()}
          />
          <SelectAll>
            <input
              type="checkbox"
              checked={allFilteredSelected}
              onChange={handleToggleAllFiltered}
              style={{accentColor: '#2153ff'}}
            />
            {t('alarms:labels:selectAll', 'Select all')}
          </SelectAll>
          {filtered().map(equip => (
            <MenuItem key={equip.id}>
              <input
                type="checkbox"
                checked={controller.isSelected(category, equip.id)}
                onChange={() => handleToggleOne(equip.id)}
                style={{accentColor: '#2153ff'}}
              />
              {equip.name}
              <SiteInfo>({equip.siteName})</SiteInfo>
            </MenuItem>
          ))}
          {filtered().length === 0 && (
            <MenuItem style={{color: '#bbb', fontStyle: 'italic'}}>
              {t('alarms:labels:NoDeviceFound', 'No devices found')}
            </MenuItem>
          )}
        </Menu>
      )}
    </DropdownWrapper>
  )
}

const Container = styled.div``

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 2%;
`

const DropdownWrapper = styled.div`
  position: relative;
  width: 100%;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 2%;
`

const DropdownButton = styled.button`
  width: calc(100% - 40px);
  padding: 10px 14px;
  border: 1px solid ${colors.system.grey_1};
  border-radius: 6px 0 0 6px;
  background: #fff;
  text-align: left;
  font-family: FavoritStd-Book;
  font-size: 14px;
  cursor: pointer;
`

const ClearButton = styled.button`
  width: 40px;
  padding: 0 8px;
  height: 40px;
  border: 1px solid ${colors.system.grey_1};
  border-left: none;
  border-radius: 0 6px 6px 0;
  background: #faf7f7;
  color: #d04444;
  font-weight: bold;
  font-size: 18px;
  cursor: pointer;
  &:hover {
    background: #fce4e4;
  }
`

const Menu = styled.div`
  position: absolute;
  z-index: 10;
  width: 100%;
  top: 110%;
  left: 0;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 2px 14px 0 rgba(0, 0, 0, 0.11);
  max-height: 270px;
  overflow-y: auto;
`

const MenuItem = styled(Label)`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  gap: 10px;
  font-size: 15px;
  &:hover {
    background: #f5f7fa;
  }
`

const SelectAll = styled(MenuItem)`
  font-weight: bold;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 2;
`

const SearchInput = styled.input`
  display: block;
  margin: 8px 12px 8px 12px;
  width: calc(100% - 24px);
  padding: 6px 10px;
  font-size: 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;
`

const SiteInfo = styled.span`
  margin-left: 8px;
  color: #888;
  font-size: 13px;
`

export default DevicesList
